const en = {
  common: {
    appName: 'Digima',
    details: 'Details',
    search: 'Search',
    skip: 'Skip',
    cancel: 'Cancel',
    ok: 'OK',
    confirm: 'Confirm',
  },
  startup: {
    connectionErrorTitle: 'Unable to connect',
    connectionErrorMessage: 'We could not reach the server. Check your connection and try again.',
    retry: 'Retry',
  },
  aiTranscriptionConsent: {
    title: 'About using AI features',
    intro:
      'The input Strategy Note feature in this app sends transcript text generated from your inputted audio to Amazon Web Services, Inc. for AI Strategy Note generation.',
    bulletSentData: '• Data sent: transcript text from your inputted audio',
    bulletDestination: '• Destination: Amazon Web Services, Inc.',
    bulletPurpose:
      '• Purpose: automated AI Strategy Note generation only (not used to train Amazon Web Services, Inc. AI models)',
    declineConsequence:
      'Input and AI Strategy Note are integrated in this feature. If you choose "Decline", input will not start.',
    privacyPrefix: 'For details, see our',
    privacyLink: 'Privacy Policy',
    privacySuffix: '.',
    agree: 'Agree & continue',
    decline: 'Decline',
  },
  auth: {
    signInAlertTitle: 'Important Notice',
    signInSubtitle:
      'This application is currently being rolled out sequentially to accounts with a Digima AI Agent contract.\nWe plan to provide access to non-contract accounts in the future, so please wait a moment.\nFor details or questions, please contact support.',
    signInButton: 'Sign In with Digima',
    errors: {
      invalidCallback: 'Invalid callback URL',
      stateMismatch: 'State mismatch',
      loginFailed: 'Login failed',
      userDisabled: 'This account has been disabled and cannot sign in.',
      accountLocked: 'Your account has been locked. Reset your password to unlock it.',
      failedToGetAuthLink: 'Failed to get authorization link',
      loginNoData: 'Login failed: no data returned',
      oauth: {
        accessDenied: 'Access was denied. You may not have permission to use this application.',
        serverError: 'The authentication server encountered an error. Please try again later.',
        unknown: 'An unexpected authentication error occurred. Please try again.',
      },
    },
  },
  contacts: {
    loadError: 'Failed to load contacts',
    empty: 'No contacts found',
    emptyDescription: 'No contacts matched your search. Please try a different keyword.',
    picker: {
      searchPlaceholder: 'Search for contacts...',
      viewProfile: 'View profile',
      seeMemo: 'See memo',
      memoSection: 'Memo',
      seeMore: 'See more',
      detailsSection: 'Details',
      profileScreenTitle: 'Details for %{name}',
      statusLabel: 'Status: %{name}',
    },
    lastAction: {
      label: '%{action} %{time}',
      types: {
        contactSmsInboundMessageReceived: 'SMS received',
        contactSmsOutboundMessageSent: 'SMS sent',
        contactEmailClicked: 'Email clicked',
        contactEmailInboxOpened: 'Email opened',
        contactPageVisited: 'Page visited',
        contactLineInboundMessageReceived: 'LINE received',
        contactLineClicked: 'LINE clicked',
        contactSmsLinkClicked: 'SMS link clicked',
        unknown: 'Last activity',
      },
      time: {
        justNow: 'just now',
        minutesAgo: '%{count} min ago',
        hoursAgo: '%{count} hour(s) ago',
        daysAgo: '%{count} day(s) ago',
        monthsAgo: '%{count} month(s) ago',
        yearsAgo: '%{count} year(s) ago',
      },
    },
    notesSheet: {
      title: "%{name}'s memo details",
    },
    filter: {
      assignedToMe: 'Assigned to Me',
      allContacts: 'All Contacts',
    },
    profile: {
      accessDenied: 'You do not have permission to view this contact.',
      loadError: 'Failed to load contact details',
      noMemo: 'No memo to display',
      noDetails: 'No details to display',
      dateTime: 'Date time',
      meetingMemosLoadError: 'Failed to load meeting memos',
      status: 'Status',
      noStatus: 'No Status',
      statusUpdateFailed: 'Failed to update status',
      personInCharge: 'Person in Charge',
      noPersonInCharge: 'Unassigned',
      personInChargeUpdateFailed: 'Failed to update person in charge',
      personalInfo: 'Personal Information',
      workInfo: 'Work Information',
      email: 'Email',
      personalMobile: 'Mobile',
      personalLandline: 'Landline',
      age: 'Age',
      gender: 'Gender',
      company: 'Company',
      department: 'Department',
      jobTitle: 'Job Title',
      workLandline: 'Work Phone',
      workMobile: 'Work Mobile',
      attributeLabels: {
        id: 'Contact ID',
        companyId: 'Company ID',
        uid: 'Contact UID',
        firstName: 'First Name',
        firstNameKana: 'First Name Kana',
        lastName: 'Last Name',
        lastNameKana: 'Last Name Kana',
        fullName: 'Full Name',
        email: 'Email',
        age: 'Age',
        gender: 'Gender',
        personalMobileNumber: 'Personal mobile number',
        personalMobileNumberCountryCode: 'Personal mobile country code',
        personalLandlineNumber: 'Personal landline number',
        personalLandlineNumberCountryCode: 'Personal landline country code',
        personalFaxNumber: 'Personal fax number',
        personalFaxNumberCountryCode: 'Personal fax country code',
        personalCountry: 'Personal country',
        personalRegion: 'Personal region',
        personalCity: 'Personal city',
        personalPostalCode: 'Personal postal code',
        personalAddress1: 'Personal address 1',
        personalAddress2: 'Personal address 2',
        personalWebsiteUrl: 'Personal website URL',
        workCompanyName: 'Company Name',
        workWebsiteUrl: 'Company URL',
        workDepartment: 'Work department',
        workJobTitle: 'Work job title',
        workLandlineNumber: 'Work landline number',
        workLandlineNumberCountryCode: 'Work Landline Country Code',
        workMobileNumber: 'Work mobile number',
        workMobileNumberCountryCode: 'Work Mobile Country Code',
        workFaxNumber: 'Work Fax Number',
        workFaxNumberCountryCode: 'Work Fax Country Code',
        workCountry: 'Work Country',
        workRegion: 'Work Region',
        workCity: 'Work City',
        workPostalCode: 'Work Postal Code',
        workAddress1: 'Work Address 1',
        workAddress2: 'Work Address 2',
        notes: 'Notes',
        contactStatusId: 'Contact status',
        personInChargeId: 'Person In Charge',
        firstApproachedAt: 'First approached at',
        lastApproachedAt: 'Last Approached At',
        creationMethod: 'Creation Method',
        lastActionType: 'Last Action Type',
        lastActionOccurredAt: 'Last Action Occurred At',
        archivedAt: 'Added At',
        createdAt: 'Created At',
        updatedAt: 'Updated at',
        customerType: 'Customer Types',
        leadAcquisitionChannels: 'Lead Acquisition Channels',
        realEstateInquiry: {
          clientSelectedPlan: 'Client Selected Plan',
          inquiredAt: 'Time and date of inquiry',
          preferredModeOfCommunication: 'Requested mode of contact',
          preferredTimeOfCommunication: 'Requested Time of contact',
          content: 'Lead Acquisition Summary',
          yearlyIncome: 'Yearly Income',
        },
        realEstateConstructionInquiry: {
          locationDetails: 'Construction Location/Exist of Land',
          plannedTiming: 'Planned Timing for construction',
          budget: 'Construction Budget',
          requirements: 'Construction Request',
          type: 'Inquiry Type',
          modelHouseName: 'Inquiry Model House Name',
          reservationTiming: 'Requested Reservation Date/Time',
          eventName: 'Inquiry Event Name',
          eventTiming: 'Inquiry Event Date/Time',
          preferredCatalogName: 'Requested Catalog Name',
          exhibitionVenueName: 'Inquiry Exhibition Venue Name',
        },
        realEstatePurchaseInquiry: {
          propertyType: 'Buying Property Type',
          propertyName: 'Buying Property Name',
          preferredMoveInDate: 'Requested Move-in Date',
          propertyImageUrl: 'Buying Property Image',
          preferredPropertySpecification: 'Buying Requested Detail',
          preferredArea: 'Buying Desired Area',
        },
        realEstateSaleInquiry: {
          propertyType: 'Property Type For Sale',
          propertyAddress: 'Property Address For Sale',
          propertyLandArea: 'Property Land Area For Sale',
          propertyBuildingArea: 'Property Building Area For Sale',
          propertyFloorPlan: 'Property Floor Plan For Sale',
          propertyAge: 'Property Age For Sale',
          propertyVacancyStatus: 'Property Vacancy Status For Sale',
          propertyOwnerName: 'Property Owner Name For Sale',
          preferredSaleTime: 'Preferred Sale Time',
          saleReason: 'Sale Reason',
          preferredAssessmentMethod: 'Preferred Assessment Method',
          offerPrice: 'Offer Price',
          requests: 'Requests',
        },
      },
      attributeValues: {
        gender: {
          male: 'Male',
          female: 'Female',
          other: 'Other',
          notSpecified: 'Not Specified',
        },
        creationMethod: {
          api: 'API',
          import: 'Import',
          leadAcquisition: 'Lead Acquisition',
          webInterface: 'Web Interface',
          smsReceived: 'SMS received',
          lineFriends: 'LINE Friends',
          andpad: 'ANDPAD',
          kintone: 'kintone',
          sentFromFranchisor: 'Sent from franchisor',
          unknown: 'Unknown',
        },
        lastActionType: {
          emailClicked: 'Email Clicked',
          emailOpened: 'Email Opened',
          emailReceived: 'Email received',
          emailSent: 'Email sent',
          leadAcquisition: 'Lead Acquisition',
          pageVisited: 'Page Visited',
          emailUnsubscribed: 'Email Unsubscribed',
          lineClicked: 'LINE Clicked',
          smsClicked: 'SMS Clicked',
          smsReceived: 'SMS received',
          lineFriendAdded: 'LINE Friend Added',
          lineMessageReceived: 'LINE message received',
          lineMessageSent: 'LINE message sent',
          unknown: 'Unknown',
        },
      },
      notes: 'Notes',
      voiceRecordings: 'Voice Inputs',
      serverRecordings: 'Server Inputs',
    },
  },
  meetings: {
    title: 'Meeting memos',
    titleMyMemos: 'My Meeting Memos',
    titleAllMemos: 'All Meeting Memos',
    offlineBanner: 'Your mobile device is offline.',
    noInternetBanner: 'Your device is not connected to the internet.',
    noWifiBanner: 'Your device is not connected to Wi-Fi.',
    attentionBanner: 'Some meeting requires your attention.',
    aiSummaryGenerated: 'AI Strategy Note generated',
    aiSummaryInProgress: 'AI Strategy Note generation in progress',
    aiSummaryUnavailable: 'AI generated Strategy Note unavailable',
    aiSummaryOnHold: 'AI Strategy Note on hold: quota exceeded',
    pendingUpload: 'Strategy Note unavailable: No Wifi connection',
    uploadFailed: 'Upload failed',
    noRecording: 'No input',
    missingAttendees: 'Missing Attendees',
    fixIssues: 'Fix issues',
    addAttendees: 'Add Attendees',
    turnOnWifi: 'Turn on Wifi',
    changeUploadSettings: 'Change upload settings',
    loadError: 'Failed to load meetings',
    empty: 'There are no meeting memo',
    emptyDescription:
      'Meeting Memos uses AI to capture your meetings through the Digima app. Input audio, and get an AI-generated Strategy Note saved as your meeting log.',
    emptyMyDescription:
      "You don't have any meeting memos yet. Start input to create your first meeting memo.",
    emptyAllDescription:
      'There are currently no meeting memo registered for the selected person in charge.',
    emptyAction: 'Start a meeting memo input now',
    attendeeCount: '%{count} attendee(s)',
    attendeeSummary: {
      one: 'Meeting with %{name}',
      other: 'Meeting with %{name} & %{count} others',
    },
    filter: {
      myMeetings: 'My Meetings',
      allMeetings: 'All Meetings',
      personInCharge: 'Person in charge',
      selectPersonInCharge: 'Select a person in charge',
      me: 'me',
      all: 'all',
    },
    detail: {
      headerTitle: 'Meeting Memo details',
      loadError: 'Failed to load meeting details',
      titleField: 'Title',
      dateTime: 'Date time',
      endsAt: 'Ends',
      location: 'Location',
      personInCharge: 'Person in charge',
      selectPersonInCharge: 'Select a person in charge',
      attendees: 'Attendees',
      attendeesPlaceholder: 'Select at least one contact',
      attendeesCount: '%{count} contact(s) selected',
      contactPickerTitle: 'Who were you in this meeting with?',
      unknownContact: 'Unknown Contact',
      voiceRecording: 'Voice Input',
      recordingAvailable: 'Input available',
      transcription: 'Transcription',
      summary: 'AI generated Strategy Note',
      seeMore: 'See more',
      copyText: 'Copy Strategy Note',
      copiedToClipboard: 'Copied to clipboard',
      recordingSummary: {
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
        completedAt: 'Completed at',
        summary: 'Strategy Note',
        transcript: 'Transcript',
        topics: 'Topics',
      },
      summaryInProgress:
        'Strategy Note generation in progress. You will receive a push notification when available.',
      summaryWifiUnavailable: 'Unavailable: Waiting for wifi connection.',
      summaryQuotaExceeded:
        'Unavailable: Monthly minute quota exceeded. Your Strategy Note will be generated after your quota resets on %{date} at 1am.',
      summaryFailed: 'Unavailable: AI generated Strategy Note could not be created.',
      summaryUploadFailed: 'Unavailable: Upload failed.',
      summaryNotAvailableImported:
        'A Strategy Note cannot be generated for imported interaction records.',
      summaryNoSpeechDetected: 'The audio input contains no detectable speech.',
      scheduledRecordingCta: 'To generate an AI Strategy Note, start an audio input meeting now.',
      memo: 'Memo',
      memoMaxLength: 'Memo must be at most %{max} characters',
      notes: 'Notes',
      notesPlaceholder: 'Add meeting notes...',
      notesSaved: 'Notes saved',
      notesSaveFailed: 'Failed to save notes',
      saved: 'Meeting saved',
      saveFailed: 'Failed to save meeting',
      save: 'Save',
      cancel: 'Cancel',
      selectTime: 'Select time',
      month: 'Month',
      year: 'Year',
      backToDate: 'Back to Date',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      previousYear: 'Previous year',
      nextYear: 'Next year',
      hourInputAccessibilityLabel: 'Hour',
      minuteInputAccessibilityLabel: 'Minute',
    },
  },
  aiTasks: {
    title: 'AI Baton',
    emptyTitle: "Don't miss sales opportunities!\nAI baton management",
    emptyDescription: 'No baton has been passed to you right now',
    aiSuggestionsLabel: 'AI suggestions: ',
    aiSuggestionsBody:
      'From call scripts to SMS replies, Digima AI suggests what you need to work quickly and efficiently.',
    pushNotificationsLabel: 'Push notifications: ',
    pushNotificationsBody:
      'Get notified on your phone the moment something important comes in, so you never miss a chance to respond.',
    loadError: 'Failed to load AI Baton',
    urgentSectionTitle: 'Urgent: a customer has been waiting 24 hours',
    card: {
      defaultDescription: {
        manualPhoneCall: 'We secured a phone appointment with %{name}.',
        meetingAppointmentSecured: 'The meeting with %{name} is confirmed.',
        scheduleMeetingAppointment: '%{name} wants to move the meeting. Let us find another date!',
        sendDocuments: '%{name} asked for documents to be sent.',
        printMaterialsSentMail: 'The printed materials for %{name} are on their way by post.',
        propertyAvailabilityCheck:
          '%{name} asked whether the property is still available. A strong sign of intent!',
      },
    },
    filter: {
      all: 'All',
      contact: 'Contact',
      createdAt: 'Created',
      personInCharge: 'Person in charge',
      selectContact: 'Select a contact',
      selectCreatedAt: 'Select a creation date',
      selectPersonInCharge: 'Select a person in charge',
      searchContactPlaceholder: 'Search by contact name...',
      dateRange: {
        today: 'Today',
        last7Days: 'Last 7 days',
        thisWeek: 'This week',
        last30Days: 'Last 30 days',
        thisMonth: 'This month',
      },
    },
    severity: {
      urgent: 'Urgent',
      important: 'Important',
      moderatelyImportant: 'Somewhat important',
    },
    section: {
      important: 'Important',
      moderatelyImportant: 'Somewhat important',
      other: 'Other',
    },
    badge: {
      awaitingClaim: 'Awaiting claim',
      assignedToMe: 'Assigned to me',
      unassignedTooLong: 'Unassigned too long',
    },
    action: {
      claim: 'Receive Baton',
      claimAndOpen: 'Receive Baton & Open Details',
      release: 'Return Baton',
    },
    error: {
      claimFailed: 'Failed to take the baton',
      releaseFailed: 'Failed to return the baton',
    },
    releaseOthersConfirm: {
      title: "Return another person's baton?",
      body: '%{name} is currently handling this baton. Returning it removes them and makes it available to everyone.',
      bodyUnknownHolder:
        'Another person is currently handling this baton. Returning it removes them and makes it available to everyone.',
      confirm: 'Return it anyway',
      dismiss: 'Cancel',
    },
    handoffType: {
      manualPhoneCall: 'Phone call request',
      printMaterialsSentMail: 'Print materials mailed',
      meetingAppointmentSecured: 'Meeting secured',
      scheduleMeetingAppointment: 'Schedule meeting',
      sendDocuments: 'Send documents',
      propertyAvailabilityCheck: 'Property availability',
    },
    detail: {
      defaultDescription: {
        manualPhoneCall:
          'We secured a phone appointment with %{name}. When the time comes, please call the customer using the information below.',
        meetingAppointmentSecured:
          'The meeting with %{name} is confirmed. This one is for your information only, so no reply to the customer is needed. Please review the details below before the day.',
        scheduleMeetingAppointment:
          'The meeting with %{name} is confirmed. This one is for your information only, so no reply to the customer is needed. Please review the details below before the day.',
        printMaterialsSentMail:
          'The printed materials for %{name} are on their way by post. A follow-up timed to their arrival lands best. Please review the details below and contact the customer if anything is needed.',
        propertyAvailabilityCheck:
          '%{name} asked whether the property they are interested in is still available. That is a strong sign of intent. Please check availability using the details below and get back to them.',
        sendDocuments:
          '%{name} asked for printed materials to be mailed. Putting a printed catalogue in their hands is a great chance to show what your company offers. Please arrange the shipment using the details below.',
      },
      title: {
        manualPhoneCall: 'Phone call request details',
        printMaterialsSentMail: 'Print materials request details',
        meetingAppointmentSecured: 'Meeting secured details',
        scheduleMeetingAppointment: 'Schedule meeting request details',
        sendDocuments: 'Send documents request details',
        propertyAvailabilityCheck: 'Property availability request details',
      },
      subtitle: {
        assignedToMe: 'Assigned to me',
        awaitingClaim: 'Awaiting claim',
        claimedByOther: 'Assigned',
      },
      notClaimedWarning:
        'This baton has not been taken yet. Take the baton first to start working on it.',
      statusLabel: 'Status: %{status}',
      noStatus: 'No status',
      tab: {
        activity: 'Activity History',
        customer: 'Contact Info',
      },
      chip: {
        sms: 'SMS Messages',
        correspondence: 'Meetings',
      },
      meetingCard: {
        title: 'Meeting record',
        description:
          '%{user} met with %{attendees} for "%{meeting}" (summarised from the Digima mobile app recording)',
        descriptionNoAttendees:
          '%{user} handled "%{meeting}" (summarised from the Digima mobile app recording)',
        summaryTab: 'Summary',
        notesTab: 'Handwritten notes',
        noSummary: 'No summary yet',
        noNotes: 'No handwritten notes',
        readMore: 'Read more',
      },
      close: 'Close',
      call: 'Call',
      createSms: 'Create SMS',
      aiAgentName: 'Digima AI Agent',
      customerName: 'Contact',
      smsEmpty: 'No SMS messages yet',
      smsLoadError: 'Failed to load the conversation',
      meetingsLoadError: 'Failed to load the meetings',
      retry: 'Retry',
      smsSending: 'Sending…',
      correspondenceEmpty: 'No meetings yet',
      customerMemo: 'Contact Notes',
      customerFields: 'Contact Information',
      readMore: 'Read more',
      field: {
        lastName: 'Last name',
        firstName: 'First name',
        email: 'Email address',
        mobileNumber: 'Mobile number',
      },
      noContact: 'No contact is linked to this baton',
      loadError: 'Failed to load the baton',
      noPhoneNumber: 'No phone number is registered',
      callFailed: 'Could not start the call on this device',
      callConfirm: {
        title: 'Call the contact?',
        body: 'You are about to call %{name}. Continue?',
        confirm: 'Call',
        dismiss: 'Cancel',
      },
      smsCompose: {
        title: 'Send SMS',
        bodyPlaceholder: 'Enter your message',
        lastNameLabel: 'Last name',
        bodyLabel: 'Message',
        charCount: 'Approx. %{length}/600',
        cancel: 'Cancel',
        send: 'Send SMS',
        confirmTitle: 'Send this message?',
        confirmBody: 'This SMS will be sent to the contact.',
        confirm: 'Send',
        dismiss: 'Cancel',
        sendFailed: 'Failed to send the SMS',
        sent: 'SMS sent',
        bodyRequired: 'Enter a message',
        charLimit: 'This message has reached the character limit.',
      },
    },
  },
  onboarding: {
    welcome: {
      title: 'Input Meetings, Get AI Strategy Notes, Improve Follow-Ups',
      body: 'The Digima app allows you to input meeting audio and uses AI to create Strategy Notes from transcripts. These Strategy Notes will later be used to improve the AI agent capabilities and boosting your chances of a follow-up meeting.',
      nextButton: 'Next, see how it works',
      skipButton: 'Skip',
    },
    step1: {
      title: 'Step 1: Input your meeting',
      body: 'To generate a Strategy Note of your meetings, you must first input it. Start by clicking the "**Start Input**" button always displayed in the bottom menu.',
      nextButton: 'Next',
      skipButton: 'Skip',
    },
    recordIllustration: {
      turnOnWifi: 'Turn on Wifi',
      changeUploadSettings: 'Change upload settings',
      aiSummaryUnavailable: 'AI Strategy Note unavailable: monthly minute quota exceeded',
      consultationMeeting: 'Consultation meeting',
      meetingWith: 'Meeting with Daiki Fujimoto & 2 others',
      time: '13:45',
      date: 'Mar 23, 2026',
      aiSummaryInProgress: 'AI Strategy Note generation in progress',
      meetingMemosTab: 'Meeting memos',
      startRecordingTab: 'Start an input',
      settingsTab: 'Settings',
    },
    step2: {
      title: 'Step 2: Save your input',
      body: 'Once the meeting is over, simply stop the input by clicking the "**Stop**" button. A meeting memo will be created and your input will be sent to Digima so the AI Strategy Note can be generated.',
      nextButton: 'Next',
      skipButton: 'Skip',
    },
    step3: {
      title: 'Step 3: Get notified',
      body: "After Digima finishes processing your input and creating the AI Strategy Note, you'll get a notification from the Digima mobile app.",
      doneButton: 'Done',
    },
  },
  tabs: {
    contacts: 'Contacts',
    meetings: 'Meetings',
    aiTasks: 'AI Baton',
    startRecording: 'Start Input',
    settings: 'Settings',
    help: 'Help',
  },
  settings: {
    title: 'Settings',
    versionLabel: 'Version {{version}}',
    logOutButton: 'Log out',
    signOutButton: 'Sign Out',
    profileError: 'Failed to load profile',
    networkAndMobileData: 'Input Upload Settings',
    localMeetingRecordings: 'Unuploaded Device Inputs',
    quotaUsage: {
      title: 'AI Strategy Note Usage',
      subtitle: '%{month} %{year}: %{used}/%{total} minutes used',
      screenTitle: 'AI Strategy Note monthly minutes',
      description:
        "This is the number of audio input minutes that you can use per months. Audio input will still be uploaded to Digima even when the mostly quote is reached, however AI Strategy Note won't be generated until additional minutes are purchased or your montly quota is updated.",
      personalQuotaTitle: 'Your personal monthly quota',
      personalQuotaDescription:
        'This the monthly quota attached to your Digima user account. Once reached you will automatically use your company monthly quota.',
      personalQuotaLabel: 'Personal quota',
      companyQuotaTitle: 'Your company shared quota',
      companyQuotaDescription:
        'This is the monthly quota attached to your company Digima account. Pease note that this quota is shared across all Digima accounts.',
      companyQuotaLabel: 'Company shared quota',
      thisMonthUsage: 'This month minutes usage',
      quotaReached: 'Month quota reached',
      quotaResetOn: 'Quota will reset on %{date} at 1am',
      exceeded: 'Exceeded',
      minutesSuffix: ' min',
    },
    uploadSettings: {
      title: 'Input Upload Settings',
      allowMobileData: 'Allow mobile data usage',
      allowMobileDataDescription:
        'This setting allows the use of mobile data when uploading inputs to Digima. By default, the Enable mobile data setting is OFF, and inputs are uploaded only when the device is connected to a Wi-Fi network.',
    },
    storage: {
      title: 'Unuploaded Device Inputs',
      description:
        'Inputs that fail to upload to Digima for AI Strategy Note creation are temporarily saved on your device. Fix the error listed under each audio input to automatically trigger another upload attempt.',
      totalUsed: 'Total storage used',
      voiceRecordings: 'Voice Inputs',
      emptyMessage: 'No local memo inputs to display',
      dateAndTime: 'Date and time: %{date}',
      sizeAndRecorded: 'Size: %{size} - Inputted on %{date}',
      localRecording: 'Local input',
    },
    storageUsage: 'Storage Usage',
    appInfo: {
      title: 'App Info',
      privacyPolicy: 'Privacy Policy',
    },
  },
  recording: {
    actions: {
      recordVoice: 'Input Voice',
      expand: 'Expand',
      pauseRecording: 'Pause',
      resumeRecording: 'Resume',
      stopPlayback: 'Stop',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete Input',
      deleteConfirmMessage:
        'Are you sure you want to delete this input? This action cannot be undone.',
      deleteSuccess: 'Local input deleted',
      retry: 'Retry upload',
      retrySuccess: 'Meeting memo successfully updated',
      retryFailed: 'Failed to retry upload',
      uploadSuccess: 'Meeting memo successfully updated',
      meetingCreated: 'Meeting created. Input will upload when Wi-Fi is available',
      assignContacts: 'Assign Contacts',
      assignContactsSuccess: 'Input uploaded to selected contacts',
    },
    indicator: {
      recording: 'Input for Strategy Note...',
      paused: 'Paused',
    },
    liveSurface: {
      recording: 'Inputting',
      paused: 'Paused',
    },
    fullScreen: {
      title: 'Voice input',
      minimize: 'Minimize',
      starting: 'Starting...',
      recording: 'Input for Strategy Note',
      saving: 'Saving...',
      paused: 'Input for Strategy Note is paused',
      pause: 'Pause',
      resume: 'Resume',
      stop: 'Stop',
      cancelStart: 'Cancel',
    },
    remaining: {
      hours: 'About {{value}}h left',
      minutes: 'About {{value}}m left',
    },
    startConfirmation: {
      title: 'Start meeting input?',
      body: 'Are you sure you want to start this audio meeting input?',
      notePrefix: 'Please note ',
      noteBody: 'that this input will only be used to generate a meeting Strategy Note.',
      noteSuffix: ' You will not be able to playback this input later.',
      confirm: 'Yes',
      dismiss: 'No',
    },
    stopConfirmation: {
      title: 'End this meeting input?',
      body: 'Are you sure you want to end this audio meeting input?',
      confirm: 'Yes',
      dismiss: 'No',
    },
    errors: {
      microphonePermissionDenied: 'Microphone permission is required to input voice notes.',
      notificationPermissionDenied:
        'Notification permission is required to keep recording in the background.',
      recordingFailed: 'Failed to start input. Please try again.',
      encoderStalled: 'No audio input detected. The recording has been stopped.',
      recordingInterrupted: 'Input was interrupted. Please try again.',
      saveFailed: 'Failed to save the input.',
      deleteFailed: 'Failed to delete the input.',
      invalidUserOrAccount: 'User or Account information missing. Please sign in again.',
      uploadFailed: 'Failed to upload input to server.',
      loadRemoteFailed: 'Failed to load inputs from server.',
      loadAllFailed: 'Failed to load inputs.',
      assignContactsFailed: 'Failed to upload input to contacts.',
      recordingNotFound: 'Input not found.',
      lowStorageStopped: 'Voice input stopped and saved because device storage is low.',
    },
    lowStorageWarning: {
      message: 'Device storage is low. Your voice input will stop automatically soon.',
    },
    lowStorageStart: {
      title: 'Low storage',
      body: 'Low storage. A long recording may not complete. Continue?',
      confirm: 'Yes',
      dismiss: 'No',
    },
    notifications: {
      appKilled: {
        title: 'Input interrupted',
        body: 'The input was interrupted unexpectedly.',
      },
      lowStorage: {
        title: 'Voice input saved',
        body: 'Voice input stopped and saved because device storage is low.',
      },
    },
    emptyList: 'No voice inputs yet',
    contactPicker: {
      title: 'Select Contacts',
      selected: '%{count} contact(s) selected',
      confirm: 'Attach attendees',
    },
  },
  drawer: {
    status: {
      pending: 'Pending upload',
      uploading: 'Uploading...',
      uploaded: 'Uploaded',
      failed: 'Upload failed',
      pendingContact: 'Awaiting contact selection',
    },
    empty: 'No local inputs',
  },
  pushNotification: {
    errors: {
      permissionDenied:
        'Push notification permission was denied. Enable it in Settings to receive notifications.',
      registrationFailed: 'Failed to register for push notifications. Please try again.',
      unregisterFailed: 'Failed to unregister from push notifications.',
    },
  },
  notifications: {
    aiSummaryFailed: {
      title: 'Failed to generate Strategy Note',
      body: 'Turn on Wifi or change your upload setting',
    },
  },
  notificationPermissionPrompt: {
    title: 'Turn on notifications to catch important sales tasks instantly',
    body: 'Enable push notifications so you never miss meeting tasks or customer call requests that need your attention. Respond instantly even while out of the office, and never miss the timing to win an appointment.',
    enableButton: 'Turn on notifications',
    skipButton: 'Skip',
  },
} as const;

export { en };
